import FilledButtonM from "@common/components/button/FilledButtonM"
import TextButtonM from "@common/components/button/TextButtonM"
import TextM from "@common/components/text/TextM"
import { theme } from "@common/theme/theme"
import { IS_WEB } from "@common/utils/mobileUtils"
import FunnelFadeUp from "@common/components/funnel/FunnelFadeUp"
import React, { useEffect, useRef, useState } from "react"
import { Animated, Easing, ScrollView, View } from "react-native"
import { ChampionImageM } from "../../champions/ChampionImageM"
import PendingAction from "./PendingAction"

// Le temps de lire « Sam corrige ta copie » seul à l'écran, avant que le défi
// n'arrive.
const INTRO_DURATION = 2600

// La barre qui dit que Sam travaille : sans durée promise, elle ne s'arrête pas.
const WorkingBar = ({ width, style }) => {
    const slide = useRef(new Animated.Value(0)).current
    useEffect(() => {
        const loop = Animated.loop(
            Animated.timing(slide, {
                toValue: 1,
                duration: 1400,
                easing: Easing.inOut(Easing.quad),
                // Sur le web, une boucle confiée au pilote natif ne joue qu'une
                // fois : la barre s'arrêtait vide.
                useNativeDriver: !IS_WEB,
            })
        )
        loop.start()
        return () => loop.stop()
    }, [slide])

    const segment = width * 0.4

    return (
        <View
            style={[
                {
                    width,
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: theme.colors.primary + "22",
                    overflow: "hidden",
                },
                style,
            ]}
        >
            <Animated.View
                style={{
                    width: segment,
                    height: 6,
                    borderRadius: 3,
                    backgroundColor: theme.colors.primary,
                    transform: [
                        {
                            translateX: slide.interpolate({
                                inputRange: [0, 1],
                                outputRange: [-segment, width],
                            }),
                        },
                    ],
                }}
            />
        </View>
    )
}

const Centered = ({ children }) => (
    <FunnelFadeUp style={{ flex: 1, alignItems: "center", justifyContent: "center" }}>
        {children}
    </FunnelFadeUp>
)

/**
 * L'attente de la correction, en trois temps, un seul message à la fois :
 * Sam prend la copie, seul à l'écran ; puis il se range en haut et laisse la
 * place à de quoi patienter ; enfin il annonce l'issue — la correction prête,
 * avant qu'elle ne s'ouvre, ou le mot qui dit qu'elle ne viendra pas.
 */
const CorrectionWait = ({
    ready,
    endingText,
    questions,
    activityId,
    onSkip,
}) => {
    const [introDone, setIntroDone] = useState(false)
    useEffect(() => {
        const id = setTimeout(() => setIntroDone(true), INTRO_DURATION)
        return () => clearTimeout(id)
    }, [])

    const renderStage = () => {
        if (ready) {
            return (
                <Centered key="ready">
                    <ChampionImageM name="sam" variant="happy" width={120} />
                    <TextM
                        fontWeight="ExtraBold"
                        fontSize={24}
                        color={theme.colors.successDark}
                        style={{ marginTop: 16, textAlign: "center" }}
                        wrap
                    >
                        Ta correction est prête !
                    </TextM>
                </Centered>
            )
        }

        if (endingText) {
            return (
                <Centered key="ending">
                    <ChampionImageM name="sam" variant="main" width={120} />
                    <TextM
                        fontWeight="Bold"
                        fontSize={18}
                        color={theme.colors.primaryDark}
                        style={{ marginTop: 16, marginBottom: 24, textAlign: "center" }}
                        wrap
                    >
                        {endingText}
                    </TextM>
                    <FilledButtonM
                        label="Activité suivante"
                        onPress={onSkip}
                        style={{ width: null, paddingHorizontal: 12 }}
                    />
                </Centered>
            )
        }

        if (!introDone) {
            return (
                <Centered key="intro">
                    <ChampionImageM name="sam" animated isLoading width={110} />
                    <TextM
                        fontWeight="ExtraBold"
                        fontSize={24}
                        color={theme.colors.primary}
                        style={{ marginTop: 16, textAlign: "center" }}
                        wrap
                    >
                        Sam corrige ta copie
                    </TextM>
                    <TextM
                        fontWeight="SemiBold"
                        fontSize={15}
                        color={theme.colors.grey}
                        style={{ marginTop: 6, textAlign: "center" }}
                        wrap
                    >
                        Ta correction arrive dans un instant.
                    </TextM>
                    <WorkingBar width={180} style={{ marginTop: 20 }} />
                </Centered>
            )
        }

        return (
            <View key="patience" style={{ flex: 1 }}>
                <FunnelFadeUp from={-12} style={{ flexDirection: "row", alignItems: "center" }}>
                    {/* Sam animé reste à l'intro : ici, pendant un live, la
                        barre suffit à dire qu'il travaille. */}
                    <ChampionImageM name="sam" variant="main" width={44} />
                    <View style={{ marginLeft: 12 }}>
                        <TextM fontWeight="Bold" fontSize={17} color={theme.colors.primary}>
                            Sam corrige ta copie
                        </TextM>
                        <WorkingBar width={150} style={{ marginTop: 8 }} />
                    </View>
                </FunnelFadeUp>

                <ScrollView
                    style={{ flex: 1 }}
                    // Ancrée en haut : la carte grandit vers le bas quand la
                    // réponse s'affiche, les choix ne bougent pas sous le doigt.
                    contentContainerStyle={{ paddingTop: 24, paddingBottom: 16 }}
                    showsVerticalScrollIndicator={false}
                >
                    <FunnelFadeUp from={28} delay={250}>
                        <TextM
                            fontWeight="Bold"
                            fontSize={14}
                            color={theme.colors.grey}
                            style={{ marginBottom: 10, marginLeft: 4 }}
                        >
                            En attendant ta correction
                        </TextM>
                        <View
                            style={{
                                backgroundColor: theme.colors.primaryLighter,
                                borderRadius: 24,
                                paddingVertical: 20,
                                paddingHorizontal: 16,
                            }}
                        >
                            <PendingAction questions={questions} activityId={activityId} />
                        </View>
                    </FunnelFadeUp>
                </ScrollView>

                <FunnelFadeUp from={0} delay={700}>
                    <TextButtonM
                        label="Continuer sans attendre"
                        color={theme.colors.grey}
                        onPress={onSkip}
                        trackingId="correction.wait.skipped"
                        style={{ alignSelf: "center", paddingVertical: 10, paddingHorizontal: 16 }}
                    />
                </FunnelFadeUp>
            </View>
        )
    }

    return (
        <View
            style={{
                flex: 1,
                alignItems: "center",
                paddingTop: 20,
                paddingBottom: 8,
                paddingHorizontal: 20,
            }}
        >
            <View style={{ flex: 1, width: "100%", maxWidth: 480 }}>{renderStage()}</View>
        </View>
    )
}

// Rien de ce qu'elle montre ne change d'un relevé à l'autre.
export default React.memo(CorrectionWait)
